import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, FileDown, CalendarClock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatWIB } from "@/lib/time";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import TicketHistory from "@/components/TicketHistory";
import TicketPhotos from "@/components/TicketPhotos";
import MultiUserPicker from "@/components/MultiUserPicker";
import LocationPicker from "@/components/LocationPicker";
import RouteButtons from "@/components/RouteButtons";
import RescheduleDialog from "@/components/RescheduleDialog";
import WibDateTimePicker from "@/components/WibDateTimePicker";

const empty = {
  tgl_masuk: "",
  completed_at: "",
  sumber: "PERMINTAAN",
  jenis_pekerjaan: "PSB",
  no_tiket: "",
  objek_lokasi: "",
  wilayah: "",
  pic: "",
  tim_pendamping: [],
  prioritas: "P3",
  status: "OPEN",
  biaya: 0,
  keterangan: "",
  lat: null,
  lng: null,
  cancel_reason: "",
};

const REF = {
  prioritas: ["P1", "P2", "P3", "P4"],
  status: ["OPEN", "SCHEDULED", "PROSES", "DONE", "RESCHEDULE", "WAITING", "CANCEL"],
};

// Static fallback until /api/reference loads
const DEFAULT_REF = {
  sumber: [{ code: "PERMINTAAN", label: "PERMINTAAN" }, { code: "LAPORAN/GANGGUAN", label: "LAPORAN/GANGGUAN" }],
  jenis: [{ code: "PSB", label: "PSB" }, { code: "REP", label: "REP" }, { code: "REL", label: "REL" }, { code: "UPG", label: "UPG" }, { code: "NET", label: "NET" }],
};

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function normalizeTim(v) {
  if (Array.isArray(v)) return v;
  if (typeof v === "string" && v.trim()) {
    return v.split(",").map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

export default function TicketForm({ open, onOpenChange, initial, onSaved, readOnly = false, initialTab = "detail", highlight = null }) {
  const { user } = useAuth();
  const canPickUsers = user?.role === "admin" || user?.role === "koordinator";
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [ticketFull, setTicketFull] = useState(null);
  const [refData, setRefData] = useState({ sumber: DEFAULT_REF.sumber, jenis: DEFAULT_REF.jenis, prioritas: [], status: [] });
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  // Feb 2026 P0 — Wajibkan penentuan lokasi eksplisit oleh user untuk
  // tiket BARU. Default center map (GPS device / kantor) TIDAK boleh
  // dianggap sebagai lokasi pekerjaan. State ini di-set true HANYA
  // ketika onChange dari LocationPicker membawa geocoded=true.
  // Untuk EDIT tiket existing yang sudah punya wilayah tersimpan,
  // state ini di-init true supaya user tidak dipaksa geser ulang.
  const [locationConfirmedByUser, setLocationConfirmedByUser] = useState(false);
  // Ephemeral highlight ("status") triggered by clicking a status-change
  // notification. Renders a yellow flash on the STATUS field for ~2.1s
  // (3 iterations of the statusFlashYellow keyframe), then clears itself.
  const [highlightField, setHighlightField] = useState(null);

  // Reset activeTab whenever the dialog re-opens (or the requested initialTab
  // changes) so notification click → open on "photos" tab reliably; regular
  // Edit clicks default back to "detail".
  useEffect(() => {
    if (open) setActiveTab(initialTab || "detail");
  }, [open, initialTab, initial?.id]);

  // Kick off the yellow flash when the dialog opens WITH highlight set.
  // Cleared after the animation duration so re-opening later without
  // highlight doesn't re-flash.
  useEffect(() => {
    if (!open) return;
    if (highlight === "status") {
      setHighlightField("status");
      const t = setTimeout(() => setHighlightField(null), 2200);
      return () => clearTimeout(t);
    }
    setHighlightField(null);
  }, [open, highlight, initial?.id]);

  useEffect(() => {
    if (!open) return;
    api.get("/reference").then(({ data }) => {
      const norm = (arr) => (arr || []).map((s) => (typeof s === "string" ? { code: s, label: s } : s));
      setRefData({
        sumber: norm(data.sumber),
        jenis: data.jenis_pekerjaan || [],
        prioritas: norm(data.prioritas),
        status: norm(data.status),
      });
    }).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (open) {
      const src = initial || {};
      const tim = normalizeTim(src.tim_pendamping);
      const isNew = !src.id;
      setForm({ ...empty, ...src, tim_pendamping: tim });
      setTicketFull(src.id ? src : null);
      // Feb 2026 P0 — Reset flag konfirmasi lokasi setiap kali dialog dibuka.
      //   • Tiket BARU  → false (user WAJIB menggeser peta / pilih Lokasi Saya).
      //   • Edit tiket  → true (behavior existing dipertahankan — tidak
      //                    memaksa user geser map ulang untuk edit field
      //                    lain). Validasi submit isNew-only sudah handle.
      setLocationConfirmedByUser(!isNew);
      if (src.id) {
        // Fetch fresh full ticket so history/photos are up-to-date
        api.get(`/tickets/${src.id}`).then(({ data }) => setTicketFull(data)).catch(() => {});
      } else if (isNew && !src.tgl_masuk) {
        // Auto-fill TGL MASUK for new tickets using WIB (Asia/Jakarta) from server.
        // User can still edit it manually — we only set the initial value.
        api.get("/system/time").then(({ data }) => {
          const wibDate = (data?.display_time || "").slice(0, 10);
          if (/^\d{4}-\d{2}-\d{2}$/.test(wibDate)) {
            setForm((f) => (f.tgl_masuk ? f : { ...f, tgl_masuk: wibDate }));
          }
        }).catch((err) => {
          console.error("[TicketForm] failed to fetch server time for tgl_masuk:", err);
          // Fallback: leave empty so user can still open the form.
        });
      }
    }
  }, [open, initial]);

  useEffect(() => {
    if (!open || !canPickUsers) return;
    api.get("/users/lookup").then(({ data }) => setUsers(data)).catch(() => {});
  }, [open, canPickUsers]);

  const refetch = async () => {
    if (!initial?.id) return;
    try {
      const { data } = await api.get(`/tickets/${initial.id}`);
      setTicketFull(data);
    } catch (err) {
      console.error("[TicketForm] refetch failed:", err);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    // Fase — Wajibkan Objek Pekerjaan. Validasi FE: min 15 karakter
    // setelah trim. Backend juga re-validate (single source of truth).
    // Fokus inline ke field + toast supaya jelas apa yang kurang.
    const objek = (form.objek_lokasi || "").trim();
    if (objek.length < 15) {
      toast.error("Objek pekerjaan wajib diisi minimal 15 karakter.");
      const el = document.querySelector('[data-testid="tf-objek"]');
      if (el && typeof el.focus === "function") el.focus();
      return;
    }
    // Feb 2026 P0 — Tiket BARU: WAJIB lokasi dikonfirmasi user via peta.
    // Default center map tidak cukup — user harus menggeser peta atau
    // memilih Lokasi Saya sehingga reverse-geocode berhasil mengisi
    // wilayah. Edit tiket yang sudah punya lokasi tersimpan dikecualikan
    // (state di-init true di useEffect). Backend TIDAK dimodifikasi —
    // pertahankan Billing V1 & workflow tiket existing.
    const isNew = !initial?.id;
    if (isNew) {
      const latOk = typeof form.lat === "number" && !isNaN(form.lat);
      const lngOk = typeof form.lng === "number" && !isNaN(form.lng);
      const wilOk = !!(form.wilayah && String(form.wilayah).trim());
      if (!locationConfirmedByUser || !latOk || !lngOk || !wilOk) {
        toast.error("Lokasi pekerjaan wajib ditentukan. Geser peta ke lokasi pekerjaan terlebih dahulu.");
        const el = document.querySelector('[data-testid="loc-map"]');
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        return;
      }
    }
    // Frontend cancellation-reason guard (backend also enforces): whenever
    // the target status is CANCEL and the ticket is either new OR was not
    // yet CANCEL, require a 5-200 char trimmed `cancel_reason`. Prevents an
    // avoidable 400 round-trip and gives inline focus on the offending
    // field so the user knows what's missing.
    const wasCancel = (initial && initial.status === "CANCEL");
    if (form.status === "CANCEL" && !wasCancel) {
      const reason = (form.cancel_reason || "").trim();
      if (reason.length < 5 || reason.length > 200) {
        toast.error("Alasan pembatalan wajib diisi (5-200 karakter).");
        const el = document.querySelector('[data-testid="tf-cancel-reason"]');
        if (el) el.focus();
        return;
      }
    }
    setSaving(true);
    try {
      const payload = { ...form, biaya: Number(form.biaya) || 0 };
      // Legacy field removed: never send tgl_pelaksanaan
      delete payload.tgl_pelaksanaan;
      // Drop `cancel_reason` if the target status isn't CANCEL — the field
      // is only meaningful for cancellations, and sending "" on a normal
      // edit would wipe an existing reason on legacy/admin retroactive
      // paths. When status IS CANCEL, keep it (validator above enforces).
      if (payload.status !== "CANCEL") {
        delete payload.cancel_reason;
      }
      // clean empty strings so date validation passes
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      // Only ADMIN may send completed_at / tgl_masuk historical corrections
      if (user?.role !== "admin") {
        delete payload.completed_at;
      }
      if (initial?.id) {
        // On PATCH, drop fields that didn't actually change to avoid sending
        // administrative fields the role isn't allowed to touch (backend enforces too).
        const original = ticketFull || initial || {};
        Object.keys({ ...payload }).forEach((k) => {
          if (k === "id" || k === "history" || k === "photos_before" || k === "photos_after" || k === "last_reschedule") {
            delete payload[k];
            return;
          }
          const orig = original[k];
          if (JSON.stringify(orig ?? null) === JSON.stringify(payload[k] ?? null)) {
            delete payload[k];
          }
        });
        if (Object.keys(payload).length === 0) {
          toast.info("Tidak ada perubahan");
          onOpenChange(false);
          return;
        }
        await api.patch(`/tickets/${initial.id}`, payload);
        toast.success("Tiket diperbarui");
      } else {
        await api.post("/tickets", payload);
        toast.success("Tiket dibuat");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full h-10 px-3 rounded-md border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#0055FF] focus:border-transparent text-sm";

  let dialogTitle;
  if (readOnly) dialogTitle = "Detail Tiket";
  else if (initial?.id) dialogTitle = "Edit Tiket";
  else dialogTitle = "Tiket Baru";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ticket-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tighter">
            {dialogTitle}
          </DialogTitle>
        </DialogHeader>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="detail" data-testid="tab-detail">Detail</TabsTrigger>
            {initial?.id && (
              <TabsTrigger value="photos" data-testid="tab-photos">
                Foto Bukti {(() => {
                  const t = ticketFull || initial;
                  const n = (t?.photos_before?.length || 0) + (t?.photos_after?.length || 0);
                  return `(${n})`;
                })()}
              </TabsTrigger>
            )}
            {initial?.id && (
              <TabsTrigger value="history" data-testid="tab-history">
                Riwayat {((ticketFull || initial)?.history?.length) ? `(${(ticketFull || initial).history.length})` : ""}
              </TabsTrigger>
            )}
          </TabsList>
          <TabsContent value="detail">
            {(() => {
              const lr = (ticketFull || initial)?.last_reschedule;
              if (!lr) return null;
              const when = lr.at ? formatWIB(lr.at) : "";
              return (
                <div
                  data-testid="last-reschedule-banner"
                  className="mb-4 rounded-md border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 flex gap-3"
                >
                  <span className="mt-0.5 h-6 w-6 rounded-full bg-[#F59E0B]/20 text-[#B45309] flex items-center justify-center text-xs font-bold flex-shrink-0">
                    !
                  </span>
                  <div className="text-xs leading-relaxed">
                    <div className="font-bold text-[#92400E] uppercase tracking-wider text-[10px]">
                      Reschedule Terakhir · {when}
                    </div>
                    <div className="mt-0.5 text-[#78350F]">
                      <b>Alasan:</b> {lr.reason || "-"}
                      {lr.prev_pic ? <> &nbsp;·&nbsp; <b>Teknisi sebelumnya:</b> {lr.prev_pic}</> : null}
                    </div>
                    {lr.notes && (
                      <div className="mt-1 text-[#78350F] whitespace-pre-line">
                        <b>Catatan:</b> {lr.notes}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={user?.role === "admin" ? "Tgl Masuk (arsip diperbolehkan)" : "Tgl Masuk"}>
            <input
              type="date"
              value={form.tgl_masuk || ""}
              onChange={(e) => set("tgl_masuk", e.target.value)}
              className={inputCls}
              disabled={readOnly || (user?.role !== "admin" && !!initial?.id && ["DONE","CANCEL"].includes(form.status))}
              data-testid="tf-tgl-masuk"
            />
          </Field>
          {(user?.role === "admin") ? (
            <Field label="Tgl Selesai (admin only · arsip historis)">
              <WibDateTimePicker
                value={form.completed_at || null}
                onChange={(iso) => set("completed_at", iso)}
                disabled={readOnly}
                testidPrefix="tf-completed-at"
              />
            </Field>
          ) : (
            <Field label="Tgl Selesai">
              <input
                type="text"
                value={form.completed_at ? formatWIB(form.completed_at) + " WIB" : "— (otomatis diisi saat DONE)"}
                readOnly
                disabled
                className={inputCls + " bg-[#F8F9FA] text-[#9CA3AF]"}
                data-testid="tf-completed-at-view"
              />
            </Field>
          )}
          <Field label="Sumber">
            <select value={form.sumber || ""} onChange={(e) => set("sumber", e.target.value)} className={inputCls} disabled={readOnly} data-testid="tf-sumber">
              <option value="">— Pilih —</option>
              {refData.sumber.map((s) => <option key={s.code} value={s.code}>{s.code}</option>)}
            </select>
          </Field>
          <Field label="Jenis Pekerjaan">
            <select value={form.jenis_pekerjaan || ""} onChange={(e) => set("jenis_pekerjaan", e.target.value)} className={inputCls} disabled={readOnly} data-testid="tf-jenis">
              <option value="">— Pilih —</option>
              {refData.jenis.map((j) => <option key={j.code} value={j.code}>{j.code} — {j.label}</option>)}
            </select>
          </Field>
          <Field label="No. Tiket">
            <input value={form.no_tiket || ""} onChange={(e) => set("no_tiket", e.target.value)} placeholder="otomatis jika kosong" className={inputCls} disabled={readOnly} data-testid="tf-no-tiket" />
          </Field>
          <div className="md:col-span-2">
            <Field label="Wilayah — Cari Lokasi di Peta">
              <LocationPicker
                value={form.wilayah || ""}
                lat={form.lat}
                lng={form.lng}
                disabled={readOnly}
                onChange={({ lat, lng, wilayah, geocoded }) => {
                  setForm((f) => ({
                    ...f,
                    lat,
                    lng,
                    // Only overwrite wilayah when we have a resolved value from geocoding
                    ...(wilayah !== undefined ? { wilayah: wilayah || (lat == null && lng == null ? "" : f.wilayah) } : {}),
                  }));
                  // Feb 2026 P0 — Konfirmasi lokasi TIKET hanya sah bila
                  // reverse-geocode SUKSES membawa wilayah baru untuk
                  // koordinat yang dipilih user. Fail (geocoded=false) →
                  // reset konfirmasi supaya submit ditolak, kecuali user
                  // sedang mengedit tiket yang wilayah aslinya utuh (restore).
                  const wilNonEmpty = !!(wilayah && String(wilayah).trim());
                  if (geocoded && wilNonEmpty) {
                    setLocationConfirmedByUser(true);
                  } else if (!wilNonEmpty) {
                    setLocationConfirmedByUser(false);
                  }
                }}
              />
              {(() => {
                const isNew = !initial?.id;
                // Indikator hanya untuk tiket BARU. Edit tiket existing
                // tidak menampilkan indikator supaya UX tidak menakuti
                // user yang hanya edit field non-lokasi.
                if (readOnly || !isNew) return null;
                if (locationConfirmedByUser) {
                  return (
                    <div
                      className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-[#065F46] bg-[#ECFDF5] border border-[#A7F3D0] rounded px-2 py-1"
                      data-testid="tf-loc-confirmed"
                    >
                      ✓ Lokasi pekerjaan ditentukan
                    </div>
                  );
                }
                return (
                  <div
                    className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-[#92400E] bg-[#FFFBEB] border border-[#FDE68A] rounded px-2 py-1"
                    data-testid="tf-loc-unconfirmed"
                  >
                    ⚠️ Lokasi pekerjaan belum ditentukan. Geser peta ke lokasi pekerjaan.
                  </div>
                );
              })()}
              {form.wilayah && (
                <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Wilayah terdeteksi:</span>
                    <span className="inline-flex items-center gap-1 bg-[#0055FF]/10 text-[#0044CC] text-xs font-bold px-2 py-1 rounded-md" data-testid="tf-wilayah-badge">
                      {form.wilayah}
                    </span>
                  </div>
                  <RouteButtons ticket={{ lat: form.lat, lng: form.lng, wilayah: form.wilayah }} size="sm" showLabel />
                </div>
              )}
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Objek / Lokasi Pekerjaan (Deskripsi) *">
              {(() => {
                // Fase — Wajibkan Objek Pekerjaan. Validasi realtime:
                // trim → count. Kurang dari 15 → red border + hint.
                // Sudah 15+ → hint hilang. `tf-objek-lokasi` sinonim
                // dengan `tf-objek` (dipertahankan untuk regression).
                const trimmed = (form.objek_lokasi || "").trim();
                const remain = 15 - trimmed.length;
                const invalid = remain > 0;
                return (
                  <>
                    <input
                      value={form.objek_lokasi || ""}
                      onChange={(e) => set("objek_lokasi", e.target.value)}
                      className={`${inputCls} ${invalid && form.objek_lokasi !== undefined && form.objek_lokasi !== "" ? "border-[#DC2626] ring-1 ring-[#DC2626]" : ""}`}
                      disabled={readOnly}
                      required
                      minLength={15}
                      placeholder="Contoh: PSB Sidareja - Mbak Salsa"
                      data-testid="tf-objek"
                      aria-invalid={invalid}
                      aria-describedby="tf-objek-hint"
                    />
                    <p id="tf-objek-hint" className={`text-[11px] mt-1 ${invalid ? "text-[#DC2626]" : "text-[#6B7280]"}`} data-testid="tf-objek-hint">
                      {invalid
                        ? `Wajib diisi minimal 15 karakter (kurang ${remain}).`
                        : `${trimmed.length} karakter — OK.`}
                    </p>
                    {/* Fase — testid `tf-objek` = element utama;
                        removed hidden alias to prevent selector confusion. */}
                  </>
                );
              })()}
            </Field>
          </div>
          {/* Hidden input to preserve backward-compat test targeting the wilayah field */}
          <input type="hidden" value={form.wilayah || ""} readOnly data-testid="tf-wilayah" />
          <Field label="PIC (Nama Teknisi)">
            {canPickUsers ? (
              <select value={form.pic || ""} onChange={(e) => set("pic", e.target.value)} className={inputCls} disabled={readOnly} data-testid="tf-pic">
                <option value="">— Pilih PIC —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.name}>{u.name} · {u.role}</option>
                ))}
              </select>
            ) : (
              <input value={form.pic || ""} onChange={(e) => set("pic", e.target.value)} className={inputCls} disabled={readOnly || !canPickUsers} data-testid="tf-pic" />
            )}
          </Field>
          <Field label="Tim Pendamping">
            <MultiUserPicker
              value={form.tim_pendamping || []}
              onChange={(v) => set("tim_pendamping", v)}
              users={users}
              exclude={form.pic}
              disabled={readOnly || !canPickUsers}
              testid="tf-tim"
            />
          </Field>
          <Field label="Prioritas">
            <select value={form.prioritas || ""} onChange={(e) => set("prioritas", e.target.value)} className={inputCls} disabled={readOnly} data-testid="tf-prioritas">
              <option value="">— Pilih —</option>
              {(refData.prioritas.length > 0 ? refData.prioritas.map(p => p.code) : REF.prioritas).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select
              value={form.status || ""}
              onChange={(e) => set("status", e.target.value)}
              className={`${inputCls} ${highlightField === "status" ? "status-flash" : ""}`}
              disabled={readOnly}
              data-testid="tf-status"
              data-highlight={highlightField === "status" ? "true" : "false"}
            >
              <option value="">— Pilih —</option>
              {(refData.status.length > 0 ? refData.status.map(s => s.code) : REF.status).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          {form.status === "CANCEL" && (() => {
            // Cancellation reason UX:
            //   • Required when transitioning INTO CANCEL (initial not
            //     already CANCEL) — enforced client-side + backend.
            //   • Locked once the ticket has been persisted as CANCEL,
            //     unless the current user is admin (retroactive audit fix).
            //   • Legacy CANCEL tickets pre-dating the audit field render
            //     an empty locked field; admins can then fill it.
            const wasCancel = initial?.status === "CANCEL";
            const canEdit = !readOnly && (!wasCancel || user?.role === "admin");
            const reason = form.cancel_reason || "";
            const trimmed = reason.trim();
            const invalid = !wasCancel && (trimmed.length > 0) && (trimmed.length < 5 || trimmed.length > 200);
            const cancelledBy = initial?.cancelled_by;
            const cancelledAt = initial?.cancelled_at;
            return (
              <div className="md:col-span-2" data-testid="tf-cancel-reason-wrap">
                <Field label={`Alasan Pembatalan${!wasCancel ? " (wajib · 5–200 karakter)" : ""}`}>
                  <textarea
                    rows={2}
                    value={reason}
                    onChange={(e) => set("cancel_reason", e.target.value)}
                    disabled={!canEdit}
                    maxLength={200}
                    placeholder="Contoh: Client batalkan permintaan, objek tidak ditemukan, duplikat tiket…"
                    className={`${inputCls} h-auto py-2 ${invalid ? "border-[#DC2626] focus:ring-[#DC2626]/40 focus:border-[#DC2626]" : ""} ${!canEdit ? "bg-[#F8F9FA] text-[#4B5563]" : ""}`}
                    data-testid="tf-cancel-reason"
                  />
                  <div className="mt-1 flex items-center justify-between text-[10px] text-[#6B7280]">
                    <span>
                      {wasCancel && cancelledBy
                        ? `Dibatalkan oleh ${cancelledBy}${cancelledAt ? " · " + formatWIB(cancelledAt) + " WIB" : ""}`
                        : wasCancel
                          ? "Tiket CANCEL tanpa alasan tercatat (legacy)"
                          : "Wajib diisi sebelum menyimpan"}
                    </span>
                    <span className={trimmed.length > 200 ? "text-[#DC2626] font-semibold" : ""}>
                      {trimmed.length}/200
                    </span>
                  </div>
                </Field>
              </div>
            );
          })()}
          <Field label="Biaya (Rp)">
            <input type="number" min={0} value={form.biaya ?? 0} onChange={(e) => set("biaya", e.target.value)} className={inputCls} disabled={readOnly} data-testid="tf-biaya" />
          </Field>
          <div className="md:col-span-2">
            <Field label="Keterangan / Alasan">
              <textarea rows={3} value={form.keterangan || ""} onChange={(e) => set("keterangan", e.target.value)} className={`${inputCls} h-auto py-2`} disabled={readOnly} data-testid="tf-keterangan" />
            </Field>
          </div>
          <DialogFooter className="md:col-span-2 mt-2">
            {!readOnly && initial?.id && (
              (() => {
                const currentTicket = ticketFull || initial;
                const canReschedule =
                  currentTicket &&
                  currentTicket.pic &&
                  currentTicket.pic === user?.name &&
                  !["DONE", "CANCEL"].includes(currentTicket.status);
                if (!canReschedule) return null;
                return (
                  <button
                    type="button"
                    data-testid="btn-reschedule"
                    onClick={() => setRescheduleOpen(true)}
                    className="mr-auto h-10 px-4 rounded-md bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold flex items-center gap-2 transition-colors"
                  >
                    <CalendarClock className="h-4 w-4" />
                    Reschedule / Tunda Pekerjaan
                  </button>
                );
              })()
            )}
            {!readOnly && (() => {
              const objekOk = (form.objek_lokasi || "").trim().length >= 15;
              return (
                <button
                  type="submit"
                  disabled={saving || !objekOk}
                  data-testid="tf-submit"
                  className="h-10 px-5 rounded-md bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold flex items-center gap-2 transition-colors"
                  title={!objekOk ? "Objek pekerjaan wajib diisi minimal 15 karakter" : undefined}
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  Simpan
                </button>
              );
            })()}
          </DialogFooter>
        </form>
          </TabsContent>
          {initial?.id && (
            <TabsContent value="photos">
              <TicketPhotos ticket={ticketFull || initial} onChange={async () => { await refetch(); onSaved?.(); }} />
            </TabsContent>
          )}
          {initial?.id && (
            <TabsContent value="history">
              <div className="flex items-center justify-between mb-4">
                <div className="text-[11px] font-bold uppercase tracking-widest text-[#4B5563]">
                  Jejak Audit Tiket
                </div>
                <button
                  type="button"
                  data-testid="btn-export-history-pdf"
                  onClick={async () => {
                    try {
                      const resp = await api.get(`/tickets/${initial.id}/history.pdf`, { responseType: "blob" });
                      const blob = new Blob([resp.data], { type: "application/pdf" });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `riwayat_tiket_${(initial.no_tiket || initial.id).replace(/\//g, "_")}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success("PDF riwayat diunduh");
                    } catch (e) {
                      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal unduh PDF");
                    }
                  }}
                  className="h-8 px-3 rounded-md border border-[#E5E7EB] bg-white text-[#0055FF] text-xs font-semibold hover:bg-[#F8F9FA] transition-colors flex items-center gap-1.5"
                >
                  <FileDown className="h-3.5 w-3.5" />
                  Unduh PDF
                </button>
              </div>
              <TicketHistory history={(ticketFull || initial).history || []} />
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
      {initial?.id && (
        <RescheduleDialog
          open={rescheduleOpen}
          onOpenChange={setRescheduleOpen}
          ticket={ticketFull || initial}
          onDone={async () => {
            await refetch();
            onSaved?.();
            onOpenChange(false);
          }}
        />
      )}
    </Dialog>
  );
}
